import { CHEVRON_LEFT, CHEVRON_RIGHT } from '@/core/icon/collections';
import { Typography } from '@/core/ui';
import clsx from 'clsx';
import { Link } from 'react-router-dom';

type Variant = 'red' | 'chalk' | 'green';
type Rounded = 'full' | 'md' | 'none';
type Chevron = 'left' | 'right' | null;

interface BaseProps {
  label: string;
  variant?: Variant;
  rounded?: Rounded;
  chevron?: Chevron;
  className?: string;
  selected?: boolean;
  disabled?: boolean;
}

type ButtonProps = BaseProps & {
  to?: undefined;
  onClick?: () => void;
};

type LinkProps = BaseProps & {
  to: string;
  onClick?: never;
};

type ActionButtonProps = ButtonProps | LinkProps;

export const ActionButton: React.FC<ActionButtonProps> = ({
  label,
  variant = 'red',
  rounded = 'md',
  chevron = null,
  onClick,
  to,
  className,
  selected,
  disabled = false,
}) => {
  // These maps apply only when `selected` is not used
  const colorMap: Record<Variant, string> = {
    red: 'bg-danger text-chalk',
    chalk: 'bg-white text-danger border-danger',
    green: 'bg-green-600 text-white',
  };

  const roundedMap: Record<Rounded, string> = {
    full: 'rounded-full',
    md: 'rounded-2xl',
    none: 'rounded-none',
  };

  const isSelectable = typeof selected === 'boolean';

  const backgroundColor = isSelectable
    ? selected
      ? 'bg-white'
      : 'bg-transparent'
    : colorMap[variant].split(' ').find((cls) => cls.startsWith('bg-'));

  const textColor = isSelectable
    ? selected
      ? 'text-danger'
      : 'text-neutral-400'
    : colorMap[variant].split(' ').find((cls) => cls.startsWith('text-'));

  const borderColor = isSelectable
    ? selected
      ? 'border-danger'
      : 'border-neutral-400'
    : colorMap[variant].includes('border-')
      ? colorMap[variant].split(' ').find((cls) => cls.startsWith('border-'))
      : 'border-transparent';

  const baseClasses = clsx(
    'h-[108px] w-[355px] flex items-center justify-center gap-2 transition border-2',
    backgroundColor,
    borderColor,
    roundedMap[rounded],
    disabled ? 'opacity-50 cursor-not-allowed' : '',
    className,
  );

  const content = (
    <div className='flex items-center gap-4'>
      {chevron === 'left' && <CHEVRON_LEFT className='h-7 w-7' />}
      <Typography className={clsx('uppercase text-4xl leading-[32px] text-center py-9', textColor)}>
        {label}
      </Typography>
      {chevron === 'right' && <CHEVRON_RIGHT className='h-7 w-7' />}
    </div>
  );

  return to ? (
    <Link to={to} className={baseClasses}>
      {content}
    </Link>
  ) : (
    <button type='button' onClick={onClick} className={baseClasses}>
      {content}
    </button>
  );
};
